import React, { useEffect, useState, useRef } from 'react';
import { useInView, animate } from 'framer-motion';

interface AnimatedCounterProps {
  value: string;
  className?: string;
}

export const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ value, className }) => {
  const ref = useRef<HTMLSpanElement>(null);
  // Track whether animation has already fired — prevents double-animation
  const hasAnimated = useRef(false);
  // The value that was locked in when animation started
  const lockedValue = useRef<string>('');

  const isInView = useInView(ref, { once: true, margin: '-20px' });

  // Extract numeric value (removing spaces, commas, and suffix)
  const parseNumeric = (v: string) =>
    parseInt(v.replace(/[\s,]/g, '').replace(/[^0-9]/g, ''), 10) || 0;
  // Extract suffix (only non-numeric characters at the end, excluding commas and spaces)
  const parseSuffix = (v: string) => {
    const m = v.match(/[^\d\s,]+$/);
    return m ? m[0] : '';
  };

  // Display state — starts at 0
  const [display, setDisplay] = useState({ count: 0, suffix: parseSuffix(value) });

  useEffect(() => {
    // Only animate when in view AND value is known (non-empty) AND hasn't animated yet
    if (!isInView || !value || hasAnimated.current) return;

    hasAnimated.current = true;
    lockedValue.current = value;

    const target = parseNumeric(value);
    const suffix = parseSuffix(value);

    const controls = animate(0, target, {
      duration: 2,
      ease: [0.16, 1, 0.3, 1], // easeOutExpo
      onUpdate(v) {
        setDisplay({ count: Math.floor(v), suffix });
      },
    });

    return () => controls.stop();
  // Intentionally omit `value` from deps — we lock it once animation fires
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  // If admin changes value AFTER animation already played, update display instantly
  // without re-animating (avoids second run)
  useEffect(() => {
    if (hasAnimated.current && value && value !== lockedValue.current) {
      lockedValue.current = value;
      setDisplay({ count: parseNumeric(value), suffix: parseSuffix(value) });
    }
  }, [value]);

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('ru-RU').format(num).replace(/\u00A0/g, ' ');

  return (
    <span ref={ref} className={className}>
      {formatNumber(display.count)}{display.suffix}
    </span>
  );
};
